import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { ManagedPermissionRecord } from "@/lib/admin/managedPermissionsStore";
import type { RoleRecord } from "@/lib/admin/rolesStore";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, KeyRound, Loader2, Search, Shield, XCircle } from "lucide-react";
import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { Link } from "react-router-dom";

function sortRoles(items: RoleRecord[]): RoleRecord[] {
  return [...items].sort((a, b) => a.name.localeCompare(b.name));
}

function sortPerms(items: ManagedPermissionRecord[]): ManagedPermissionRecord[] {
  return [...items].sort((a, b) => {
    const c = (a.categoryName ?? "").localeCompare(b.categoryName ?? "");
    if (c !== 0) return c;
    return a.title.localeCompare(b.title) || a.name.localeCompare(b.name);
  });
}

function matchesRole(r: RoleRecord, q: string): boolean {
  const n = q.trim().toLowerCase();
  if (!n) return true;
  return r.name.toLowerCase().includes(n);
}

function matchesPerm(p: ManagedPermissionRecord, q: string): boolean {
  const n = q.trim().toLowerCase();
  if (!n) return true;
  return (
    p.name.toLowerCase().includes(n) ||
    p.title.toLowerCase().includes(n) ||
    (p.categoryName && p.categoryName.toLowerCase().includes(n))
  );
}

function groupPermsByCategory(items: ManagedPermissionRecord[]): Array<{ category: string; items: ManagedPermissionRecord[] }> {
  const by = new Map<string, ManagedPermissionRecord[]>();
  for (const p of items) {
    const key = (p.categoryName ?? "").trim() || "Uncategorized";
    const arr = by.get(key) ?? [];
    arr.push(p);
    by.set(key, arr);
  }
  const categories = Array.from(by.keys()).sort((a, b) => {
    if (a === "Uncategorized") return 1;
    if (b === "Uncategorized") return -1;
    return a.localeCompare(b);
  });
  return categories.map((category) => ({ category, items: by.get(category) ?? [] }));
}

function checkedState(selectedCount: number, totalCount: number): boolean | "indeterminate" {
  if (totalCount <= 0) return false;
  if (selectedCount <= 0) return false;
  if (selectedCount >= totalCount) return true;
  return "indeterminate";
}

export type SpatieAccessEditorProps = {
  loading: boolean;
  roleCatalog: RoleRecord[];
  permCatalog: ManagedPermissionRecord[];
  selectedRoleNames: Set<string>;
  setSelectedRoleNames: Dispatch<SetStateAction<Set<string>>>;
  selectedPermNames: Set<string>;
  setSelectedPermNames: Dispatch<SetStateAction<Set<string>>>;
};

export function SpatieAccessEditor({
  loading,
  roleCatalog,
  permCatalog,
  selectedRoleNames,
  setSelectedRoleNames,
  selectedPermNames,
  setSelectedPermNames,
}: SpatieAccessEditorProps) {
  const [availRolesQ, setAvailRolesQ] = useState("");
  const [selRolesQ, setSelRolesQ] = useState("");
  const [availPermQ, setAvailPermQ] = useState("");
  const [selPermQ, setSelPermQ] = useState("");
  const [permFeedback, setPermFeedback] = useState<{ type: "success" | "error"; message: string; id: number } | null>(null);

  const sortedRoles = useMemo(() => sortRoles(roleCatalog), [roleCatalog]);
  const sortedPerms = useMemo(() => sortPerms(permCatalog), [permCatalog]);

  const toggleRole = (name: string, checked: boolean) => {
    setSelectedRoleNames((prev) => {
      const n = new Set(prev);
      if (checked) n.add(name);
      else n.delete(name);
      return n;
    });
  };

  const togglePerm = (name: string, checked: boolean) => {
    const title = sortedPerms.find((p) => p.name === name)?.title ?? name;
    setSelectedPermNames((prev) => {
      const n = new Set(prev);
      if (checked) n.add(name);
      else n.delete(name);
      return n;
    });
    window.setTimeout(() => {
      setPermFeedback({
        type: checked ? "success" : "error",
        message: checked ? `“${title}” was added as a direct permission.` : `“${title}” was removed as a direct permission.`,
        id: Date.now(),
      });
    }, 0);
  };

  const availRoles = useMemo(() => sortedRoles.filter((r) => !selectedRoleNames.has(r.name)), [sortedRoles, selectedRoleNames]);
  const selRoles = useMemo(() => sortedRoles.filter((r) => selectedRoleNames.has(r.name)), [sortedRoles, selectedRoleNames]);
  const filtAvailRoles = useMemo(() => availRoles.filter((r) => matchesRole(r, availRolesQ)), [availRoles, availRolesQ]);
  const filtSelRoles = useMemo(() => selRoles.filter((r) => matchesRole(r, selRolesQ)), [selRoles, selRolesQ]);

  const availPerms = useMemo(() => sortedPerms.filter((p) => !selectedPermNames.has(p.name)), [sortedPerms, selectedPermNames]);
  const selPerms = useMemo(() => sortedPerms.filter((p) => selectedPermNames.has(p.name)), [sortedPerms, selectedPermNames]);
  const filtAvailPerms = useMemo(() => availPerms.filter((p) => matchesPerm(p, availPermQ)), [availPerms, availPermQ]);
  const filtSelPerms = useMemo(() => selPerms.filter((p) => matchesPerm(p, selPermQ)), [selPerms, selPermQ]);

  const categoryToAllPermissionNames = useMemo(() => {
    const by = new Map<string, string[]>();
    for (const p of sortedPerms) {
      const key = (p.categoryName ?? "").trim() || "Uncategorized";
      const arr = by.get(key) ?? [];
      arr.push(p.name);
      by.set(key, arr);
    }
    return by;
  }, [sortedPerms]);

  const groupedAvailPerms = useMemo(() => groupPermsByCategory(filtAvailPerms), [filtAvailPerms]);
  const groupedSelPerms = useMemo(() => groupPermsByCategory(filtSelPerms), [filtSelPerms]);

  const togglePermCategory = (category: string, side: "available" | "selected") => {
    const names = categoryToAllPermissionNames.get(category) ?? [];
    if (names.length === 0) return;
    const addAll =
      side === "available" ? !names.some((n) => selectedPermNames.has(n)) : !names.every((n) => selectedPermNames.has(n));
    setSelectedPermNames((prev) => {
      const next = new Set(prev);
      if (addAll) {
        for (const n of names) next.add(n);
      } else {
        for (const n of names) next.delete(n);
      }
      return next;
    });
    window.setTimeout(() => {
      setPermFeedback({
        type: addAll ? "success" : "error",
        message: addAll
          ? `“${category}” permissions were added as direct permissions.`
          : `“${category}” permissions were removed as direct permissions.`,
        id: Date.now(),
      });
    }, 0);
  };

  const toggleAllPermissions = (side: "available" | "selected") => {
    const isAdding = side === "available";
    const names = sortedPerms.map((p) => p.name);

    setSelectedPermNames((prev) => {
      if (isAdding) {
        const next = new Set(prev);
        for (const n of names) next.add(n);
        return next;
      }
      return new Set();
    });

    window.setTimeout(() => {
      setPermFeedback({
        type: isAdding ? "success" : "error",
        message: isAdding ? "All permissions were added as direct permissions." : "All direct permissions were removed.",
        id: Date.now(),
      });
    }, 0);
  };

  useEffect(() => {
    if (!permFeedback) return;
    const t = window.setTimeout(() => setPermFeedback(null), 4200);
    return () => window.clearTimeout(t);
  }, [permFeedback]);

  if (loading) {
    return (
      <div className="flex justify-center py-16 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3 rounded-2xl border-2 border-violet-500/25 bg-gradient-to-br from-violet-500/[0.07] via-background to-background p-4 shadow-sm dark:from-violet-500/15 dark:border-violet-500/30">
        <div className="flex gap-3">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-500/15 text-violet-700 dark:text-violet-300"
            aria-hidden
          >
            <Shield className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-foreground">Roles</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Spatie roles stored in <code className="text-[10px]">model_has_roles</code> (users can have multiple roles).
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-stretch">
          <div className="flex min-h-0 flex-col rounded-xl border border-violet-500/20 bg-background/85 dark:bg-violet-950/30 p-3 gap-2 shadow-sm">
            <div className="flex items-center justify-between gap-2 shrink-0">
              <span className="text-sm font-semibold text-foreground">Available</span>
              <span className="text-[11px] text-muted-foreground tabular-nums">
                {filtAvailRoles.length}/{availRoles.length}
              </span>
            </div>
            <div className="relative shrink-0">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
              <Input
                className="glass-input h-8 pl-8 text-xs"
                placeholder="Search available…"
                value={availRolesQ}
                onChange={(e) => setAvailRolesQ(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="min-h-[100px] max-h-[min(32vh,280px)] overflow-y-auto space-y-1.5 pr-0.5">
              {sortedRoles.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">No roles loaded.</p>
              ) : availRoles.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">All roles assigned.</p>
              ) : filtAvailRoles.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">No matches.</p>
              ) : (
                filtAvailRoles.map((r) => (
                  <div key={r.id} className="flex items-start gap-2 rounded-md border border-border/50 bg-background/80 p-2">
                    <Checkbox
                      checked={selectedRoleNames.has(r.name)}
                      onCheckedChange={(c) => toggleRole(r.name, c === true)}
                      className="mt-0.5"
                      id={`sp-ae-role-av-${r.id}`}
                    />
                    <Label htmlFor={`sp-ae-role-av-${r.id}`} className="text-xs font-medium text-foreground cursor-pointer leading-snug">
                      {r.name}
                    </Label>
                  </div>
                ))
              )}
            </div>
          </div>
          <div className="flex min-h-0 flex-col rounded-xl border border-violet-500/20 bg-background/85 dark:bg-violet-950/30 p-3 gap-2 shadow-sm">
            <div className="flex items-center justify-between gap-2 shrink-0">
              <span className="text-sm font-semibold text-foreground">Selected</span>
              <span className="text-[11px] text-muted-foreground tabular-nums">
                {filtSelRoles.length}/{selRoles.length}
              </span>
            </div>
            <div className="relative shrink-0">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
              <Input
                className="glass-input h-8 pl-8 text-xs"
                placeholder="Search selected…"
                value={selRolesQ}
                onChange={(e) => setSelRolesQ(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="min-h-[100px] max-h-[min(32vh,280px)] overflow-y-auto space-y-1.5 pr-0.5">
              {selRoles.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">Select roles on the left.</p>
              ) : filtSelRoles.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">No matches.</p>
              ) : (
                filtSelRoles.map((r) => (
                  <div key={r.id} className="flex items-start gap-2 rounded-md border border-border/50 bg-background/80 p-2">
                    <Checkbox
                      checked={selectedRoleNames.has(r.name)}
                      onCheckedChange={(c) => toggleRole(r.name, c === true)}
                      className="mt-0.5"
                      id={`sp-ae-role-sel-${r.id}`}
                    />
                    <Label htmlFor={`sp-ae-role-sel-${r.id}`} className="text-xs font-medium text-foreground cursor-pointer leading-snug">
                      {r.name}
                    </Label>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3 rounded-2xl border-2 border-emerald-500/25 bg-gradient-to-br from-emerald-500/[0.07] via-background to-background p-4 shadow-sm dark:from-emerald-500/15 dark:border-emerald-500/30">
        <div className="flex gap-3">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
            aria-hidden
          >
            <KeyRound className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-foreground">Direct permissions</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Permissions attached only to this user in <code className="text-[10px]">model_has_permissions</code> (in addition to role
              permissions).
            </p>
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">
            Use the category row to select all permissions in that group, or pick them one by one below.
          </p>
          <div className="relative">
            <AnimatePresence mode="wait">
              {permFeedback ? (
                <motion.div
                  key={permFeedback.id}
                  role="status"
                  initial={{ opacity: 0, y: 10, filter: "blur(4px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0, y: -8, filter: "blur(4px)" }}
                  transition={{ type: "spring", stiffness: 420, damping: 32, mass: 0.6 }}
                  className={cn(
                    "flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-sm shadow-sm backdrop-blur-[2px]",
                    permFeedback.type === "success"
                      ? "border-emerald-500/40 bg-emerald-500/[0.12] text-emerald-950 dark:border-emerald-400/35 dark:bg-emerald-500/15 dark:text-emerald-50"
                      : "border-red-500/45 bg-red-500/[0.12] text-red-950 dark:border-red-400/40 dark:bg-red-500/15 dark:text-red-50",
                  )}
                >
                  {permFeedback.type === "success" ? (
                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-emerald-600 dark:text-emerald-400" aria-hidden />
                  ) : (
                    <XCircle className="w-4 h-4 shrink-0 mt-0.5 text-red-600 dark:text-red-400" aria-hidden />
                  )}
                  <span className="leading-snug">{permFeedback.message}</span>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-stretch">
          <div className="flex min-h-0 flex-col rounded-xl border border-emerald-500/20 bg-background/85 dark:bg-emerald-950/30 p-3 gap-2 shadow-sm">
            <div className="flex items-center justify-between gap-2 shrink-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-foreground">Available</span>
                {availPerms.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-1.5 text-[10px] text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-950/40"
                    onClick={() => toggleAllPermissions("available")}
                  >
                    Select All
                  </Button>
                )}
              </div>
              <span className="text-[11px] text-muted-foreground tabular-nums">
                {filtAvailPerms.length}/{availPerms.length}
              </span>
            </div>
            <div className="relative shrink-0">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
              <Input
                className="glass-input h-8 pl-8 text-xs"
                placeholder="Search available…"
                value={availPermQ}
                onChange={(e) => setAvailPermQ(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="min-h-[120px] max-h-[min(40vh,360px)] overflow-y-auto space-y-1.5 pr-0.5">
              {sortedPerms.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">
                  No permissions in catalog. See{" "}
                  <Link to="/permissions" className="text-primary underline">
                    Permissions
                  </Link>
                  .
                </p>
              ) : availPerms.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">All permissions assigned directly.</p>
              ) : filtAvailPerms.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">No matches.</p>
              ) : (
                groupedAvailPerms.map((g) => (
                  <div key={g.category} className="space-y-1.5">
                    <div className="sticky top-0 z-10 -mx-1 px-1 py-1 bg-background/90 backdrop-blur border-b border-border/40">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`sp-ae-perm-cat-av-${g.category}`}
                          checked={checkedState(
                            (categoryToAllPermissionNames.get(g.category) ?? []).filter((n) => selectedPermNames.has(n)).length,
                            (categoryToAllPermissionNames.get(g.category) ?? []).length,
                          )}
                          onCheckedChange={() => togglePermCategory(g.category, "available")}
                        />
                        <Label
                          htmlFor={`sp-ae-perm-cat-av-${g.category}`}
                          className="text-[11px] font-semibold text-muted-foreground cursor-pointer select-none"
                        >
                          {g.category}
                        </Label>
                      </div>
                    </div>
                    <div className="pl-4 space-y-1.5">
                      {g.items.map((p) => (
                        <div key={p.name} className="flex items-start gap-2 rounded-md border border-border/50 bg-background/80 p-2">
                          <Checkbox
                            checked={selectedPermNames.has(p.name)}
                            onCheckedChange={(c) => togglePerm(p.name, c === true)}
                            className="mt-0.5"
                            id={`sp-ae-perm-av-${p.name}`}
                          />
                          <div className="min-w-0 flex-1">
                            <Label htmlFor={`sp-ae-perm-av-${p.name}`} className="text-xs font-medium text-foreground cursor-pointer line-clamp-2">
                              {p.title}
                            </Label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          <div className="flex min-h-0 flex-col rounded-xl border border-emerald-500/20 bg-background/85 dark:bg-emerald-950/30 p-3 gap-2 shadow-sm">
            <div className="flex items-center justify-between gap-2 shrink-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-foreground">Selected</span>
                {selPerms.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-1.5 text-[10px] text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                    onClick={() => toggleAllPermissions("selected")}
                  >
                    Deselect All
                  </Button>
                )}
              </div>
              <span className="text-[11px] text-muted-foreground tabular-nums">
                {filtSelPerms.length}/{selPerms.length}
              </span>
            </div>
            <div className="relative shrink-0">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
              <Input
                className="glass-input h-8 pl-8 text-xs"
                placeholder="Search selected…"
                value={selPermQ}
                onChange={(e) => setSelPermQ(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="min-h-[120px] max-h-[min(40vh,360px)] overflow-y-auto space-y-1.5 pr-0.5">
              {selPerms.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">Check permissions on the left to attach them directly.</p>
              ) : filtSelPerms.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">No matches.</p>
              ) : (
                groupedSelPerms.map((g) => (
                  <div key={g.category} className="space-y-1.5">
                    <div className="sticky top-0 z-10 -mx-1 px-1 py-1 bg-background/90 backdrop-blur border-b border-border/40">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`sp-ae-perm-cat-sel-${g.category}`}
                          checked={checkedState(
                            (categoryToAllPermissionNames.get(g.category) ?? []).filter((n) => selectedPermNames.has(n)).length,
                            (categoryToAllPermissionNames.get(g.category) ?? []).length,
                          )}
                          onCheckedChange={() => togglePermCategory(g.category, "selected")}
                        />
                        <Label
                          htmlFor={`sp-ae-perm-cat-sel-${g.category}`}
                          className="text-[11px] font-semibold text-muted-foreground cursor-pointer select-none"
                        >
                          {g.category}
                        </Label>
                      </div>
                    </div>
                    <div className="pl-4 space-y-1.5">
                      {g.items.map((p) => (
                        <div key={p.name} className="flex items-start gap-2 rounded-md border border-border/50 bg-background/80 p-2">
                          <Checkbox
                            checked={selectedPermNames.has(p.name)}
                            onCheckedChange={(c) => togglePerm(p.name, c === true)}
                            className="mt-0.5"
                            id={`sp-ae-perm-sel-${p.name}`}
                          />
                          <div className="min-w-0 flex-1">
                            <Label htmlFor={`sp-ae-perm-sel-${p.name}`} className="text-xs font-medium text-foreground cursor-pointer">
                              {p.title}
                            </Label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
